<template>
  <TabGroup
    v-if="product"
    class="product-tabs"
    :tabs="tabGroupEntries"
    :active="activeEntry"
  >
    <!-- Highlights -->
    <template #highlights>
      <OfferHighlights :offer="offer" />
    </template>

    <!-- Delivered Items -->
    <template #deliveredItems>
      <DeliveredItems :offer="offer" />
    </template>

    <!-- Features / Technical Details -->
    <template #features>
      <FeatureList :offer="offer" />
    </template>

    <!-- Produktsicherheit -->
    <template #productSecurity>
      <ProductSecurity :offer="offer" />
    </template>
  </TabGroup>
</template>

<script setup lang="ts">
import { offerHasUsps } from '~/lib/goliath/offer-usps';
import DeliveredItems from '~/components/product-detail/tabs/delivered-items.vue';
import FeatureList from '~/components/product-detail/tabs/feature-list.vue';
import OfferHighlights from '~/components/product-detail/tabs/offer-highlights.vue';
import ProductSecurity from '~/components/product-detail/tabs/product-security.vue';
import TabGroup from '~/components/tab-group.vue';

export type ProductTabProps = {
  offer: Offer;
  activeEntry?: number;
};

const props = withDefaults(defineProps<ProductTabProps>(), {
  activeEntry: 0,
});

const product = computed(() => props.offer?.product);
const standardProduct = computed(() => getStandardProduct(product.value));
const bundleProduct = computed(() => getBundleProduct(product.value));

const containedProducts = computed<(OfferStandardProduct | OfferBundleProduct)[]>(() => {
  if (!product.value) return [];
  if (bundleProduct.value) {
    return bundleProduct.value.bundledProducts || [];
  }
  if (standardProduct.value) {
    return [standardProduct.value];
  }
  return [];
});

const hasHighlights = computed(() => offerHasUsps(props.offer));

const hasDeliveredItems = computed(() =>
  containedProducts.value.some((p) => p?.deliveredItems && p.deliveredItems.length)
);

const hasFeatures = computed(() =>
  containedProducts.value.some(
    (p) =>
      (p?.features && p.features.length) ||
      (p?.eans?.length || 0) > 0 ||
      p?.commonCharger?.possible === true
  )
);

const hasSecurity = computed(() =>
  containedProducts.value.some((p) => {
    const family = p?.family;
    const warranty = family && 'warranty' in family ? family.warranty : undefined;
    return (
      (family &&
        ('manufacturerContact' in family || 'manufacturerContactEu' in family) &&
        (family.manufacturerContact || family.manufacturerContactEu)) ||
      p?.securityInformationUrl ||
      warranty?.additionalCommercialGuarantee ||
      warranty?.softwareUpdates ||
      warranty?.repairabilityClass
    );
  })
);

const tabGroupEntries = computed(() => {
  const data = [];
  if (hasHighlights.value) {
    data.push({ name: 'highlights', label: 'Highlights' });
  }
  if (hasDeliveredItems.value) {
    data.push({ name: 'deliveredItems', label: 'Lieferumfang' });
  }
  if (hasFeatures.value) {
    data.push({ name: 'features', label: 'Technische Details' });
  }
  if (hasSecurity.value) {
    data.push({ name: 'productSecurity', label: 'Produktsicherheit' });
  }
  return data;
});
</script>
